// The `data-ui-directive` frame the gateway writes into the `POST /api/chat`
// UI-message stream when a worker calls `present_decision`. A decision turn is
// carried ENTIRELY by this frame — the gateway emits no text for it — so a
// client that drops the frame renders an empty reply.
//
// This module is the wire contract only: the shapes a client can rely on, plus
// a tolerant reader that narrows an untrusted parsed frame. The gateway keeps
// its own stricter emission-side validation (clamping lengths, rejecting
// placeholder calls); those are normalisation concerns a reader must not
// re-litigate. What is shared — and must not drift — are the type declarations
// below, which `apps/web/lib/ui-directives.ts` re-exports.

/** The wire `type` of a UI-directive stream part. */
export const UI_DIRECTIVE_PART_TYPE = "data-ui-directive" as const;

/** Which modality raised the directive. */
export type UiDirectiveSource = "voice" | "text";

export type UiDirectiveState =
  | { status: "pending" }
  | { status: "submitting"; optionIndex: number }
  | { status: "selected"; optionIndex: number; acknowledgement: "pending" | "complete" | "error" }
  | { status: "dismissed" }
  | { status: "error"; message: string };

export interface UiDirectiveV1<Name extends string = string, Args = unknown, State = UiDirectiveState> {
  version: 1;
  name: Name;
  callId: string;
  args: Args;
  state: State;
}

export interface UiDirectiveResultV1<Name extends string = string, Result = unknown> {
  version: 1;
  name: Name;
  callId: string;
  result: Result;
}

export interface PresentDecisionArgs {
  title: string;
  options: { label: string; detail: string }[];
}

export type PresentDecisionDirectiveV1 = UiDirectiveV1<"present_decision", PresentDecisionArgs>;
export type PresentDecisionResultV1 = UiDirectiveResultV1<
  "present_decision",
  { status: "selected"; optionIndex: number }
>;

/** Context-specific work a customer-owned Ezra could perform after this answer. */
export interface PresentInstallOfferArgs {
  actions: string[];
}

/** A one-way surface: it is displayed and settled immediately, never answered. */
export type PresentInstallOfferDirectiveV1 = UiDirectiveV1<
  "present_install_offer",
  PresentInstallOfferArgs,
  { status: "presented" }
>;

/** The install-offer sibling of `UiDirectiveFrameV1`. */
export interface InstallOfferDirectiveFrameV1 {
  directive: PresentInstallOfferDirectiveV1;
  source: UiDirectiveSource;
}

/** The `data` payload of a `data-ui-directive` stream part. */
export interface UiDirectiveFrameV1 {
  directive: PresentDecisionDirectiveV1;
  source: UiDirectiveSource;
}

const MIN_OPTIONS = 2;
const MAX_OPTIONS = 4;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const nonEmpty = (value: unknown): value is string =>
  typeof value === "string" && value.trim().length > 0;

const inRange = (value: unknown, count: number): value is number =>
  typeof value === "number" && Number.isInteger(value) && value >= 0 && value < count;

function parseArgs(input: unknown): PresentDecisionArgs | null {
  if (!isRecord(input) || !nonEmpty(input.title) || !Array.isArray(input.options)) return null;
  const { options } = input;
  if (options.length < MIN_OPTIONS || options.length > MAX_OPTIONS) return null;
  const parsed: PresentDecisionArgs["options"] = [];
  for (const option of options) {
    if (!isRecord(option) || !nonEmpty(option.label) || !nonEmpty(option.detail)) return null;
    parsed.push({ label: option.label, detail: option.detail });
  }
  return { title: input.title, options: parsed };
}

function parseInstallOfferArgs(input: unknown): PresentInstallOfferArgs | null {
  if (!isRecord(input) || !Array.isArray(input.actions)) return null;
  if (input.actions.length < 1 || input.actions.length > 3) return null;
  const actions: string[] = [];
  for (const action of input.actions) {
    if (!nonEmpty(action)) return null;
    actions.push(action);
  }
  return { actions };
}

function parseState(input: unknown, optionCount: number): UiDirectiveState | null {
  if (!isRecord(input)) return null;
  switch (input.status) {
    case "pending":
      return { status: "pending" };
    case "dismissed":
      return { status: "dismissed" };
    case "submitting":
      return inRange(input.optionIndex, optionCount)
        ? { status: "submitting", optionIndex: input.optionIndex }
        : null;
    case "selected":
      if (!inRange(input.optionIndex, optionCount)) return null;
      return input.acknowledgement === "pending" ||
        input.acknowledgement === "complete" ||
        input.acknowledgement === "error"
        ? { status: "selected", optionIndex: input.optionIndex, acknowledgement: input.acknowledgement }
        : null;
    case "error":
      return nonEmpty(input.message) ? { status: "error", message: input.message } : null;
    default:
      return null;
  }
}

/**
 * Narrow the `data` payload of a `data-ui-directive` frame, or return `null` if
 * it is not one this contract version understands.
 *
 * Deliberately tolerant of UNKNOWN extra fields (a newer gateway may add them)
 * but strict about every field a consumer reads — so a returned frame is always
 * safe to render. Returns a fresh object; never aliases the input.
 */
export function parseUiDirectiveFrame(input: unknown): UiDirectiveFrameV1 | null {
  if (!isRecord(input)) return null;
  if (input.source !== "voice" && input.source !== "text") return null;
  const directive = input.directive;
  if (!isRecord(directive)) return null;
  if (directive.version !== 1 || directive.name !== "present_decision") return null;
  if (!nonEmpty(directive.callId)) return null;
  const args = parseArgs(directive.args);
  if (!args) return null;
  const state = parseState(directive.state, args.options.length);
  if (!state) return null;
  return {
    directive: { version: 1, name: "present_decision", callId: directive.callId, args, state },
    source: input.source,
  };
}

/**
 * Narrow a one-way install-offer frame. Kept separate from the decision reader
 * so existing consumers retain the exact decision type they already handle.
 */
export function parseInstallOfferDirectiveFrame(
  input: unknown,
): InstallOfferDirectiveFrameV1 | null {
  if (!isRecord(input)) return null;
  if (input.source !== "voice" && input.source !== "text") return null;
  const directive = input.directive;
  if (!isRecord(directive)) return null;
  if (directive.version !== 1 || directive.name !== "present_install_offer") return null;
  if (!nonEmpty(directive.callId)) return null;
  const args = parseInstallOfferArgs(directive.args);
  if (!args) return null;
  if (!isRecord(directive.state) || directive.state.status !== "presented") return null;
  return {
    directive: {
      version: 1,
      name: "present_install_offer",
      callId: directive.callId,
      args,
      state: { status: "presented" },
    },
    source: input.source,
  };
}

// --- Answering a card ------------------------------------------------------
//
// A decision is answered on the SAME `POST /api/chat` route that delivered it —
// there is no separate decision endpoint. The answer rides as a `data-ui-result`
// part on the LAST message of the request, and that message must carry NO
// non-empty text: a message with both is read as an ordinary turn, which
// DISMISSES the pending card instead of settling it.
//
// The gateway replies on the same stream: a `data-ui-directive` frame in state
// `selected` first, then the worker's answer as text — so answering a card can
// itself yield another card, and one reader handles both.

/** The wire `type` of the stream part that carries an answer back to the gateway. */
export const UI_DIRECTIVE_RESULT_PART_TYPE = "data-ui-result" as const;

/** The `data-ui-result` part, as it rides on the request. */
export interface UiDirectiveResultPartV1 {
  type: typeof UI_DIRECTIVE_RESULT_PART_TYPE;
  data: PresentDecisionResultV1;
}

/**
 * A `POST /api/chat` message that answers a decision. Exactly one part, and no
 * text part — see the note above.
 */
export interface DecisionResultMessageV1 {
  role: "user";
  parts: [UiDirectiveResultPartV1];
}

/**
 * Build the message that settles `directive` with the option at `optionIndex`.
 *
 * Throws `RangeError` when the index is not one this directive offers — the one
 * thing the directive itself decides. Whether the card is still PENDING is the
 * gateway's authority (it holds the live call), so a directive's own `state` is
 * deliberately not gated on here: a consumer may hold a stale snapshot and still
 * be right, and refusing locally would invent a rule the wire does not have. An
 * answer to a settled card is refused with `decision_not_pending`.
 */
export function buildDecisionResultMessage(
  directive: PresentDecisionDirectiveV1,
  optionIndex: number,
): DecisionResultMessageV1 {
  if (!inRange(optionIndex, directive.args.options.length)) {
    throw new RangeError(
      `optionIndex ${String(optionIndex)} is not one of this decision's ${String(directive.args.options.length)} options.`,
    );
  }
  return {
    role: "user",
    parts: [{
      type: UI_DIRECTIVE_RESULT_PART_TYPE,
      data: {
        version: 1,
        name: "present_decision",
        callId: directive.callId,
        result: { status: "selected", optionIndex },
      },
    }],
  };
}

/**
 * Every reason the chat route refuses an answer, carried in the error
 * envelope's `details.reason`.
 *
 * The gateway names its refusals through these constants, so it cannot throw a
 * reason this contract does not define. It cannot make the reverse guarantee:
 * `details` is untyped on the wire, so nothing at the type level would stop a
 * VALUE here changing — that is held by a test in the gateway pinning the
 * emitted bytes as literals. Branch on these constants, not on strings of your
 * own, and `isDecisionRefusalReason` will tell you when you meet one you do not
 * know.
 */
export const DECISION_REFUSAL_REASONS = {
  /** 409 — no live call with that `callId`. Answered twice, or talked past and dismissed. */
  notPending: "decision_not_pending",
  /** 400 — decisions are a prompt-worker surface; a hosted worker raises none. */
  invalidAgent: "invalid_decision_agent",
  /** 400 — the `data-ui-result` payload is malformed or its option is out of range. */
  invalidResult: "invalid_decision_result",
} as const;

export type DecisionRefusalReason =
  (typeof DECISION_REFUSAL_REASONS)[keyof typeof DECISION_REFUSAL_REASONS];

const decisionRefusalReasons: readonly string[] = Object.values(DECISION_REFUSAL_REASONS);

/** Narrow an untrusted `details.reason` to a decision refusal. */
export function isDecisionRefusalReason(value: unknown): value is DecisionRefusalReason {
  return typeof value === "string" && decisionRefusalReasons.includes(value);
}
